<template>
  <div id="findPayments">
    <div class="row mb-4">
      <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
        <div class="idb-block">
          <!-- Header -->
          <div class="idb-block-title">
            <h2>
              Search Payments
              <span class="pull-right">
                <a :href="helpUrl" target="_blank">
                  <i class="far fa-question-circle d-none"></i>
                </a>
              </span>
              <favourite-icon></favourite-icon>
            </h2>
          </div>
          <!-- Main content -->
          <div class="idb-block-content">
            <div class="row">
              <div class="col-md-2">
                <strong>From Processing Date</strong>
              </div>
              <div class="col-md-2">
                <strong>To Processing Date</strong>
              </div>
              <div v-if="this.isSystemUser || this.isConnectedMerchantAdmin || this.isMasterCustomerAdmin" class="col-md-2">
                <strong>Clients</strong>
              </div>
            </div>
            <div class="row form-group">
              <div class="col-md-2">
                <vuejsDatepicker
                  ref="createdFrom"
                  name="createdFrom"
                  id="createdFrom"
                  v-model="processingDateFrom"
                  format="dd/MM/yyyy"
                  :bootstrap-styling="true"
                  v-on:input="checkDates"
                  @focusin.native="fromDateOnfocus"
                  :monday-first="true"
                ></vuejsDatepicker>
              </div>
              <div class="col-md-2">
                <vuejsDatepicker
                  ref="createdTo"
                  name="createdTo"
                  id="createdTo"
                  v-model="processingDateTo"
                  format="dd/MM/yyyy"
                  :bootstrap-styling="true"
                  v-on:input="checkDates"
                  @focusin.native="toDateOnfocus"
                  :monday-first="true"
                ></vuejsDatepicker>
              </div>
              <div v-if="this.isSystemUser || this.isConnectedMerchantAdmin || this.isMasterCustomerAdmin" class="col-md-2">
                <b-select v-model.trim="selectedCustomerType" :options="customerTypes" />
              </div>
            </div>
            <div v-if="invalidDateRange === true" class="row form-group">
              <div class="col-md-4">
                <span
                  class="form-text small mandatory"
                >The "From Processing Date" cannot be after the "To Processing Date"</span>
              </div>
            </div>
            <div class="row">
              <div class="col-md-1">
                <strong>Sort Code</strong>
              </div>
              <div class="col-md-1"></div>
              <div class="col-md-2">
                <strong>Account Number</strong>
              </div>
              <div class="col-md-2">
                <strong>Account Name</strong>
              </div>
              <div class="col-md-1"></div>
              <div class="col-md-1">
                <strong>Exact Match</strong>
              </div>
              <div class="col-md-2">
                <strong>Amount From</strong>
              </div>
              <div class="col-md-2">
                <strong>Amount To</strong>
              </div>
            </div>
            <div class="row form-group">
              <div class="col-md-1">
                <input
                  name="sortCode"
                  type="text"
                  v-model="sortCode"
                  class="form-control"
                  style="width: 100px;"
                  maxlength="6"
                  @blur="onSortCodeBlur($v.sortCode)"
                  @focus="sortCodeBlurFired = false"
                  @input="checkForNumber"
                  autocomplete="off"
                />
                <span v-if="$v.sortCode.$dirty && sortCodeBlurFired" name="Sort Code">
                  <small
                    class="form-text small mandatory"
                    v-if="!$v.sortCode.minLen || !$v.sortCode.maxLen"
                  >Sort Code must contain 6 numbers</small>
                </span>
              </div>
              <div class="col-md-1"></div>
              <div class="col-md-2">
                <input
                  name="accountNumber"
                  type="text"
                  v-model="accountNumber"
                  class="form-control"
                  style="width: 100px;"
                  maxlength="8"
                  @blur="onAccountNumberBlur($v.accountNumber)"
                  @focus="accountNumberBlurFired = false"
                  @input="checkForNumber"
                  autocomplete="off"
                />
                <span v-if="$v.accountNumber.$dirty && accountNumberBlurFired" name="Account Number">
                  <small
                    class="form-text small mandatory"
                    v-if="!$v.accountNumber.minLen || !$v.accountNumber.maxLen"
                  >Account Number must contain 8 numbers</small>
                </span>
              </div>
              <div class="col-md-2">
                <input
                  name="accountName"
                  v-model="accountName"
                  class="form-control"
                  style="width: 200px;"
                />
              </div>
              <div class="col-md-1"></div>
              <div class="col-md-1">
                <p-check class="p-switch p-fill" color="primary" v-model="exactAccountNameSearch"></p-check>
              </div>
              <div class="col-md-2">
                <input
                  name="amountFrom"
                  type="number"
                  v-model="amountFrom"
                  class="form-control"
                  style="width: 100px;"
                  @blur="checkAmounts"
                  @input="checkForNumber"
                />
                <span v-if="!$v.amountFrom.decimal" name="Amount From">
                  <small
                    class="form-text small mandatory"
                    v-if="!$v.amountFrom.decimal"
                  >Amount From must be a whole number or a monetary value</small>
                </span>
              </div>
              <div class="col-md-2">
                <input
                  name="amountTo"
                  type="number"
                  v-model="amountTo"
                  class="form-control"
                  style="width: 100px;"
                  @blur="checkAmounts"
                  @input="checkForNumber"
                />
                <span v-if="!$v.amountTo.decimal" name="Amount To">
                  <small
                    class="form-text small mandatory"
                    v-if="!$v.amountTo.decimal"
                  >Amount To must be a whole number or a monetary value</small>
                </span>
              </div>
            </div>
            <div v-if="invalidAmountRange === true" class="row form-group">
              <div class="col-md-8"></div>
              <div class="col-md-4">
                <span
                  class="form-text small mandatory"
                >The "From Amount" cannot be more than the "To Amount"</span>
              </div>
            </div>
          </div>
          <!-- Footer -->
          <div class="idb-block-footer">
            <div class="row form-group">
              <div class="col-md-2">
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="this.canSearch === false"
                  v-on:click="searchPayments"
                >Search</button>
              </div>
              <div class="col-md-10">
                <button
                  type="button"
                  class="btn btn-outline-primary pull-right"
                  v-on:click="clearSearchParams"
                >Clear Search Criteria</button>
              </div>
            </div>
            <div v-show="searchComplete === true" class="row">
              <div class="col-md-12">
                <vue-good-table
                  ref="payments"
                  :columns="columns"
                  :rows="rows"
                  :lineNumbers="false"
                  :totalRows="totalRecords"
                  @on-row-click="navToSubmission"
                  @on-page-change="onPageChange"
                  @on-per-page-change="onPerPageChange"
                  @on-sort-change="onSortChange"
                  :pagination-options="{
                    enabled: true,
                    perPage: rowsPerPage,
                    dropdownAllowAll: false,
                    setCurrentPage: currentPage,
                    jumpFirstOrLast: true
                  }"
                  :sort-options="{
                    enabled: true,
                    initialSortBy: this.sortCriteria
                  }"
                  styleClass="vgt-table striped bordered"
                >
                  <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field === 'submissionStatus'">
                      <span
                        v-b-popover.hover.top="statusToolTip(props.row.submissionStatus)"
                        v-bind:class="getStatusClass(props.row.submissionStatus)"
                        v-bind:style="getStatusStyle(props.row.submissionStatus)"
                      ></span>
                    </span>
                    <span v-else>{{props.formattedRow[props.column.field]}}</span>
                  </template>
                  <div slot="table-actions">
                    <b-button
                      @click.prevent="createPaymentCsvFile"
                      class
                      variant="link"
                      v-b-popover.hover.top.d500="'Export search results to CSV'"
                    >
                      <i class="fas fa-file-export pointer dimmedIcon"></i>
                    </b-button>
                  </div>
                </vue-good-table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { numeric, decimal, minLength, maxLength } from 'vuelidate/lib/validators'
import bacsMixin from '@/Lib/BacsMixin.js'
import roles from '@/Assets/Constants/roles'
import { mapGetters } from 'vuex'

export default {
  name: 'searchPayments',
  mixins: [
    bacsMixin
  ],
  computed: {
    ...mapGetters(['selectedCustomer']),
    isSystemUser () { return this.$store.getters.isInRole(roles.LZAdmin) || this.$store.getters.isInRole(roles.Finance) || this.$store.getters.isInRole(roles.Implementations) },
    isConnectedMerchantAdmin () { return this.$store.getters.isInRole(roles.ConnectedMerchantAdmin) },
    isMasterCustomerAdmin () { return this.$store.getters.isInRole(roles.MasterCustomerAdmin) }
  },

  data () {
    return {
      canSearch: false,
      maxRows: 1000,
      paygateId: '',
      userId: '',
      disabledDates: {},
      invalidDateRange: false,
      invalidAmountRange: false,
      processingDateFrom: '',
      processingDateTo: '',
      sortCode: '',
      prevSortCode: '',
      accountNumber: '',
      prevAccountNumber: '',
      accountName: '',
      exactAccountNameSearch: false,
      amountFrom: '',
      prevAmountFrom: '',
      amountTo: '',
      prevAmountTo: '',
      columns: [
        {
          label: 'SubmissionId',
          field: 'submissionId',
          hidden: true
        },
        {
          label: 'File No.',
          field: 'fileNumber',
          hidden: true
        },
        {
          label: 'PaygateId',
          field: 'paygateId',
          hidden: true
        },
        {
          label: 'GroupId',
          field: 'groupId',
          hidden: true
        },
        {
          label: 'Client',
          field: 'customerName'
        },
        {
          label: 'Group',
          field: 'groupName'
        },
        {
          label: 'Type',
          field: 'networkName'
        },
        {
          label: 'Dest SC',
          field: 'thirdPartySortCode'
        },
        {
          label: 'Dest Acc',
          field: 'thirdPartyAccountNumber'
        },
        {
          label: 'Dest Acc Name',
          field: 'thirdPartyAccountName'
        },
        {
          label: 'User Ref',
          field: 'userReference'
        },
        {
          label: 'Orig SC',
          field: 'originatorSortCode'
        },
        {
          label: 'Orig Acc',
          field: 'originatorAccountNumber'
        },
        {
          label: 'TX Code',
          field: 'transactionCode'
        },
        {
          label: 'Amount (£)',
          field: 'amount',
          type: 'decimal'
        },
        {
          label: 'Proc Date',
          field: 'processingDate',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd\'T\'HH:mm:ss',
          dateOutputFormat: 'dd/MM/yyyy'
        },
        {
          label: 'Pmt Date',
          field: 'paymentDate',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd\'T\'HH:mm:ss',
          dateOutputFormat: 'dd/MM/yyyy'
        },
        {
          label: 'Status',
          field: 'submissionStatus'
        }
      ],
      exportColumns: [
        {
          label: 'Group',
          field: 'groupName'
        },
        {
          label: 'Type',
          field: 'networkName'
        },
        {
          label: 'Dest SC',
          field: 'thirdPartySortCode'
        },
        {
          label: 'Dest Acc',
          field: 'thirdPartyAccountNumber'
        },
        {
          label: 'Dest Acc Name',
          field: 'thirdPartyAccountName'
        },
        {
          label: 'User Ref',
          field: 'userReference'
        },
        {
          label: 'Orig SC',
          field: 'originatorSortCode'
        },
        {
          label: 'Orig Acc',
          field: 'originatorAccountNumber'
        },
        {
          label: 'TX Code',
          field: 'transactionCode'
        },
        {
          label: 'Amount (£)',
          field: 'amount',
          type: 'decimal'
        },
        {
          label: 'Proc Date',
          field: 'processingDate',
          type: 'date',
          dateInputFormat: 'YYYY-MM-DDTHH:mm:ss',
          dateOutputFormat: 'DD/MM/YYYY'
        },
        {
          label: 'Pmt Date',
          field: 'paymentDate',
          type: 'date',
          dateInputFormat: 'YYYY-MM-DDTHH:mm:ss',
          dateOutputFormat: 'DD/MM/YYYY'
        }
      ],
      rows: [],
      totalRecords: 0,
      searchComplete: false,
      currentPage: 1,
      rowsPerPage: 10,
      helpUrl: '',
      sortCodeBlurFired: false,
      accountNumberBlurFired: false,
      customerTypes: {},
      selectedCustomerType: 1,
      sortCriteria: {}
    }
  },

  methods: {
    async searchPayments () {
      if (this.checkDates() === false || this.$v.$invalid === true) {
        return
      }

      if (this.checkAmounts() === false || this.$v.$invalid === true) {
        return
      }

      if (this.checkSearchCriteria() === false) {
        return
      }

      this.searchComplete = false
      this.rows = []

      var amountFromNum
      var amountToNum
      if (this.amountFrom !== '') {
        amountFromNum = Number(this.amountFrom)
      }
      if (this.amountTo !== '') {
        amountToNum = Number(this.amountTo)
      }

      var paymentSearchParams = JSON.stringify({
        processingDateFrom: this.processingDateFrom !== '' ? this.processingDateFrom : '01/01/0001',
        processingDateTo: this.processingDateTo !== '' ? this.processingDateTo : '01/01/0001',
        maxRows: this.maxRows,
        paygateId: this.paygateId,
        thirdPartySortCode: this.sortCode,
        thirdPartyAccountNumber: this.accountNumber,
        thirdPartyAccountName: this.accountName,
        exactAccountNameSearch: this.exactAccountNameSearch,
        amountFrom: amountFromNum,
        amountTo: amountToNum,
        customerType: this.selectedCustomerType
      })

      this.$store.dispatch('setSearchParams', paymentSearchParams)

      var response = await axios({
        method: 'POST',
        url: process.env.VUE_APP_BACS_API_URL + 'bacs/search/payments/',
        data: paymentSearchParams,
        showload: true
      })

      if (response.data !== null) {
        this.totalRecords = response.data.length
        this.rows = response.data
        this.currentPage = 1
      }

      this.searchComplete = true
    },

    clearSearchParams () {
      this.selectedGroupId = ''
      this.selectedGroupText = 'Please Select'
      this.processingDateFrom = ''
      this.processingDateTo = ''
      this.sortCode = ''
      this.accountNumber = ''
      this.prevAccountNumber = ''
      this.prevSortCode = ''
      this.accountName = ''
      this.exactAccountNameSearch = false
      this.amountFrom = ''
      this.amountTo = ''
      this.maxRows = 1000
      this.rows = []
      this.searchComplete = false
      this.selectedCustomerType = 1
      this.invalidAmountRange = false
      this.invalidDateRange = false
    },

    async initComboData () {
      this.clearSearchParams()

      this.userId = this.$store.getters.getClaim('sub').value
      if (this.isSystemUser) {
        this.paygateId = this.selectedCustomer
      } else {
        this.paygateId = this.$store.getters.getClaim('paygate_id').value
      }

      this.selectedCustomerType = 1
    },

    async getEnums () {
      var response = await axios.get(`${process.env.VUE_APP_BACS_API_URL}bacs/search/getDropdownValues`)
      this.customerTypes = response.data.customerTypes
    },

    checkDates () {
      var datesOk = true
      if (this.processingDateFrom !== '' && this.processingDateTo !== '') {
        var fromDate = new Date(this.processingDateFrom)
        var toDate = new Date(this.processingDateTo)

        if (fromDate > toDate) {
          this.invalidDateRange = true
          datesOk = false
        } else {
          this.invalidDateRange = false
        }
      }

      return datesOk
    },

    checkAmounts () {
      var amountsOk = true
      if (this.amountFrom !== '' && this.amountTo !== '') {
        var amountFromNum = Number(this.amountFrom)
        var amountToNum = Number(this.amountTo)
        if (amountFromNum > amountToNum) {
          this.invalidAmountRange = true
          amountsOk = false
        } else {
          this.invalidAmountRange = false
        }
      }

      return amountsOk
    },

    // vue-the-mask control seems to have odd behaviour pasting values into it after clearing it's value. Value is shown in control but model not updated.
    // Ditched this control for standard input control with regex checks.
    checkForNumber (e) {
      const stringValue = e.target.value.toString()
      if (stringValue !== '') {
        if (e.target.name === 'accountNumber') {
          if (!stringValue.match(/^\d{1,8}$/)) {
            this.accountNumber = this.prevAccountNumber
          }
          this.prevAccountNumber = this.accountNumber
        } else if (e.target.name === 'sortCode') {
          if (!stringValue.match(/^\d{1,6}$/)) {
            this.sortCode = this.prevSortCode
          }
          this.prevSortCode = this.sortCode
        }
      }
      else if (e.target.name === 'amountFrom') {
        if (!stringValue.match(/^\d*(\.\d{1,2})?$/)) {
          this.amountFrom = this.prevAmountFrom
        }
        this.prevAmountFrom = this.amountFrom
      } else {
        if (!stringValue.match(/^\d*(\.\d{1,2})?$/)) {
          this.amountTo = this.prevAmountTo
        }
        this.prevAmountTo = this.amountTo
      }
    },

    checkSearchCriteria () {
      var criteriaEntered = true

      if (this.processingDateFrom === '' &&
        this.processingDateTo === '' &&
        this.sortCode === '' &&
        this.accountNumber === '' &&
        this.accountName === '' &&
        this.amountFrom === '' &&
        this.amountTo === '') {
        criteriaEntered = false
        this.$snapbar.w('Please enter some search criteria.')
      }

      return criteriaEntered
    },

    navToSubmission (params) {
      this.$store.dispatch('setSubmissionId', params.row.submissionId)
      this.$store.dispatch('setActionId', undefined)
      this.$store.dispatch('setFileNumber', 1)
      this.$store.dispatch('setSearchResults', this.rows)
      this.$store.dispatch('setCustomerPaygateId', params.row.paygateId)
      const subUrl = `/payments/search/submissionWrapper?returnPage=searchPayments&submissionId=${params.row.submissionId}&fileNumber=${params.row.fileNumber}`
      this.$router.push(subUrl)
    },

    onPageChange (params) {
      this.currentPage = params.currentPage
      this.$store.dispatch('setCurrentSearchResultsPage', this.currentPage)
    },

    onPerPageChange (params) {
      this.rowsPerPage = params.currentPerPage
      this.$store.dispatch('setSearchRowsPerPage', this.rowsPerPage)
      this.searchPayments()
    },

    onSortChange (params) {
      this.sortCriteria = {
          field: params[0].field,
          type: params[0].type
        }
      
      console.log('SET sortCriteria: ' + JSON.stringify(this.sortCriteria))
      this.$store.dispatch('setSortCriteria', this.sortCriteria)
    },

    getStatusClass (submissionStatus) {
      var iconClass = ''
      switch (submissionStatus) {
        case 'Created':
          iconClass = 'fa fa-plus'
          break
        case 'Signed':
          iconClass = 'fa fa-file-signature'
          break
        case 'Approved':
          iconClass = 'fa fa-thumbs-up'
          break
        case 'complete':
          iconClass = 'fa fa-check-circle'
          break
        case 'rejected':
          iconClass = 'fa fa-times-circle'
          break
        case 'Cancelled':
          iconClass = 'fa fa-trash-alt'
          break
        case 'CreditDue':
          iconClass = 'fa fa-pause'
          break
        case 'CreditSuccessful':
          iconClass = 'fa fa-check-circle'
          break
        case 'CreditFailed':
          iconClass = 'fa fa-exclamation-circle'
          break
      }
      return iconClass
    },

    getStatusStyle (submissionStatus) {
      var colour = ''
      switch (submissionStatus) {
        case 'complete':
        case 'CreditSuccessful':
          colour = 'color: green;'
          break
        case 'rejected':
        case 'CreditFailed':
          colour = 'color: red;'
          break
        case 'warning':
          colour = 'color: #DD6B55;'
          break
        case 'Signed':
          colour = 'color: #606266;'
          break
      }
      return colour
    },

    statusToolTip (submissionStatus) {
      var toolTip = ''
      switch (submissionStatus) {
        case 'complete':
          toolTip = 'Complete'
          break
        case 'rejected':
          toolTip = 'Rejected'
          break
        case 'warning':
          toolTip = 'Warning'
          break
        default:
          toolTip = submissionStatus
          break
      }
      return toolTip
    },

    onSortCodeBlur (element) {
      element.$touch()
      this.sortCodeBlurFired = true
    },

    onAccountNumberBlur (element) {
      element.$touch()
      this.accountNumberBlurFired = true
    },

    async createPaymentCsvFile () {
      try {
        var url = process.env.VUE_APP_BACS_API_URL + 'bacs/report/createPaymentSearchResultsCsvFile'
        var response = await axios({
          url: url,
          method: 'POST',
          data: this.rows,
          responseType: 'blob', // important
          showload: true
        })
        this.saveFile(response)
      } catch (ex) {
        console.log(JSON.stringify(ex))
      }
    }
  },

  watch: {
    selectedCustomer () { this.initComboData() }
  },

  created () {
    var today = new Date()
    today.setDate(today.getDate() + 39)
    this.disabledDates.from = today
  },

  async mounted () {
    document.getElementById('createdFrom').style.backgroundColor = 'white'
    document.getElementById('createdTo').style.backgroundColor = 'white'
    this.helpUrl = process.env.VUE_APP_DOCUMENTATION_ROOT_URL + '/payments/search/searchpayments/'

    await this.initComboData()
    await this.getEnums()

    var useSearchParams = this.$route.query.useSearchParams
    console.log('useSearchParams: ' + useSearchParams)
    if (useSearchParams === undefined) {
      this.$store.dispatch('setSearchParams', null)
      this.$store.dispatch('setSortCriteria', null)
      this.$store.dispatch('setCurrentSearchResultsPage', 1)
    } else {
      try {
        var searchParams = JSON.parse(this.$store.getters.searchParams)
        this.sortCriteria = this.$store.getters.sortCriteria
        console.log('GET sortCriteria: ' + JSON.stringify(this.sortCriteria))

        this.processingDateFrom = searchParams.processingDateFrom !== '01/01/0001' ? searchParams.processingDateFrom : ''
        this.processingDateTo = searchParams.processingDateTo !== '01/01/0001' ? searchParams.processingDateTo : ''
        this.accountName = searchParams.thirdPartyAccountName
        this.exactAccountNameSearch = searchParams.exactAccountNameSearch
        this.sortCode = searchParams.thirdPartySortCode
        this.accountNumber = searchParams.thirdPartyAccountNumber
        this.amountFrom = searchParams.amountFrom > 0 ? searchParams.amountFrom : ''
        this.amountTo = searchParams.amountTo > 0 ? searchParams.amountTo : ''
        this.maxRows = searchParams.maxRows
        this.rows = this.$store.getters.searchResults
        this.selectedCustomerType = searchParams.customerType
        this.totalRecords = this.rows.length
        this.searchComplete = true
        this.currentPage = this.$store.getters.currentSearchResultsPage
        this.rowsPerPage = this.$store.getters.searchRowsPerPage
      } catch (ex) {
        // Exception thrown if user returns to search results and decides, in a way only QA can, to refresh the browser.
        const query = Object.assign({}, this.$route.query)
        delete query.useSearchParams
        this.$router.replace({ query })
        this.$store.dispatch('setSearchParams', null)
        this.$store.dispatch('setSortCriteria', null)
        this.$store.dispatch('setCurrentSearchResultsPage', 1)
      }
    }
    this.canSearch = true
  },
  validations () {
    return {
      amountFrom: { decimal },
      amountTo: { decimal },
      sortCode: { numeric, minLen: minLength(6), maxLen: maxLength(6) },
      accountNumber: { numeric, minLen: minLength(8), maxLen: maxLength(8) }
    }
  }
}
</script>
